import { Flex } from '@servicetitan/anvil2';
import { AxiosResponse } from 'axios';
import { PdfRenderer } from './PdfRenderer';
import { SignatureNotReceived } from './SignatureNotReceived';
import { useEffect, useRef } from 'react';
import { FinancingApi, LoanDocument } from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { SignatureDataStore } from './SignatureDataStore';
import { DecisionDataStore } from '../decision';
import { ServerError, LoadingScreen } from '@servicetitan/financing-utils-shared';

export const Container = () => {
    const [apiClient] = useDependencies(FinancingApi);
    const { decisionData } = DecisionDataStore.useStore();
    const { signatureData, setSignatureData } = SignatureDataStore.useStore();
    const startTimeRef = useRef<number>(Date.now());

    const { skipSignatureButtonClicked } = signatureData;
    const loanId = decisionData?.loanId ?? '0';

    const generateLoanDocumentResult = useQuery<
        AxiosResponse<LoanDocument>,
        ServerError,
        AxiosResponse<LoanDocument>,
        string[]
    >({
        queryKey: ['loansGenerateLoanDocuments', loanId],
        queryFn: () =>
            apiClient.loansGenerateLoanDocuments(loanId, {
                loanId,
            }),
        retry: (failureCount, error) => {
            if (
                typeof error !== 'string' &&
                'response' in error &&
                error.response?.status === 404
            ) {
                const elapsedTime = Date.now() - startTimeRef.current;
                if (elapsedTime >= 300000) {
                    return false;
                }
                return true;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex, error) => {
            if (
                typeof error !== 'string' &&
                'response' in error &&
                error.response?.status === 404
            ) {
                return 30000;
            }
            return 1000;
        },
    });

    const baseUrl = window.location.origin;
    const documentDownloadLink = generateLoanDocumentResult.data?.data?.documentDownloadLink;
    const pdfLink = documentDownloadLink
        ? documentDownloadLink.includes('/api/financing/')
            ? baseUrl + documentDownloadLink
            : baseUrl + '/api/financing' + documentDownloadLink
        : null;

    useEffect(() => {
        startTimeRef.current = Date.now();
    }, [loanId]);

    useEffect(() => {
        if (generateLoanDocumentResult.isSuccess) {
            setSignatureData(prev => ({ ...prev, loanDocumentLoaded: true }));
        }
    }, [generateLoanDocumentResult.isSuccess, setSignatureData]);

    useEffect(() => {
        return () => {
            setSignatureData(prev => ({
                ...prev,
                skipSignatureButtonClicked: false,
                loanDocumentLoaded: false,
            }));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Flex
            direction="column"
            alignSelf="center"
            alignItems="center"
            gap="6"
            style={{ marginTop: '20px', overflow: 'hidden' }}
        >
            {skipSignatureButtonClicked ? (
                <SignatureNotReceived />
            ) : generateLoanDocumentResult.isLoading ? (
                <LoadingScreen message="Retrieving your loan documents..." />
            ) : (
                <PdfRenderer fileName={pdfLink} />
            )}
        </Flex>
    );
};
